import * as amplitude from '@amplitude/analytics-browser';
import { getAnalytics, logEvent as firebaseLogEvent, setUserId as firebaseSetUserId, setUserProperties as firebaseSetUserProperties } from 'firebase/analytics';

/**
 * Analytics Event Interface
 */
interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
}

/**
 * User Traits Interface
 */
interface UserTraits {
  user_id: string;
  username?: string;
  display_name?: string;
  email?: string;
  auth_provider?: 'google' | 'apple';
  signup_date?: string;
  [key: string]: any;
}

/**
 * Analytics Service
 * Facade pattern supporting both Amplitude and Firebase Analytics
 */
class AnalyticsService {
  private initialized = false;
  private amplitudeEnabled = false;
  private firebaseEnabled = false;
  private firebaseAnalytics: any = null;
  private consentGiven = false;
  private queuedEvents: AnalyticsEvent[] = [];

  /**
   * Initialize analytics services
   */
  async initialize(config: {
    amplitudeApiKey?: string;
    userId?: string;
    enableAmplitude?: boolean;
    enableFirebase?: boolean;
  }) {
    const { amplitudeApiKey, userId, enableAmplitude = true, enableFirebase = true } = config;

    console.log('📊 Initializing Analytics Service...', {
      amplitudeApiKey: amplitudeApiKey ? '***' : 'not provided',
      userId,
      enableAmplitude,
      enableFirebase
    });

    try {
      // Initialize Amplitude
      if (enableAmplitude && amplitudeApiKey) {
        amplitude.init(amplitudeApiKey, userId, {
          defaultTracking: {
            sessions: true,
            pageViews: false, // We'll track manually for better control
            formInteractions: false,
            fileDownloads: false
          },
          logLevel: import.meta.env.DEV ? amplitude.Types.LogLevel.Debug : amplitude.Types.LogLevel.Warn,
        });
        
        this.amplitudeEnabled = true;
        console.log('✅ Amplitude initialized successfully');
      }

      // Initialize Firebase Analytics (if not already initialized)
      if (enableFirebase) {
        try {
          const { app } = await import('../config/firebase');
          this.firebaseAnalytics = getAnalytics(app);
          this.firebaseEnabled = true;
          
          if (userId) {
            firebaseSetUserId(this.firebaseAnalytics, userId);
          }
          
          console.log('✅ Firebase Analytics initialized successfully');
        } catch (error) {
          console.warn('⚠️ Firebase Analytics initialization failed:', error);
        }
      }

      this.initialized = true;
      
      // Process queued events if consent was given
      if (this.consentGiven && this.queuedEvents.length > 0) {
        console.log(`📤 Processing ${this.queuedEvents.length} queued events...`);
        this.queuedEvents.forEach(event => this.track(event));
        this.queuedEvents = [];
      }

    } catch (error) {
      console.error('❌ Analytics initialization failed:', error);
      this.initialized = false;
    }
  }

  /**
   * Set cookie consent status
   */
  setConsent(granted: boolean) {
    console.log('🍪 Analytics consent:', granted);
    this.consentGiven = granted;

    // If consent revoked, clear queued events
    if (!granted) {
      this.queuedEvents = [];
    }

    // If consent granted and we have queued events, process them
    if (granted && this.initialized && this.queuedEvents.length > 0) {
      console.log(`📤 Processing ${this.queuedEvents.length} queued events after consent...`);
      this.queuedEvents.forEach(event => this.track(event));
      this.queuedEvents = [];
    }
  }

  /**
   * Track an event
   */
  track(event: AnalyticsEvent) {
    const { name, properties = {} } = event;

    // If not initialized or no consent, queue the event
    if (!this.initialized || !this.consentGiven) {
      this.queuedEvents.push(event);
      console.log('📋 Event queued (awaiting initialization/consent):', name);
      return;
    }

    // Add common properties
    const enrichedProperties = {
      ...properties,
      environment: import.meta.env.VITE_ENVIRONMENT || 'production',
      timestamp: new Date().toISOString(),
      platform: 'web'
    };

    try {
      // Track in Amplitude
      if (this.amplitudeEnabled) {
        amplitude.track(name, enrichedProperties);
      }

      // Track in Firebase
      if (this.firebaseEnabled && this.firebaseAnalytics) {
        // Firebase has naming restrictions, so sanitize event name
        const firebaseEventName = this.sanitizeEventName(name);
        firebaseLogEvent(this.firebaseAnalytics, firebaseEventName, enrichedProperties);
      }

      if (import.meta.env.DEV) {
        console.log('📊 Analytics Event:', name, enrichedProperties);
      }
    } catch (error) {
      console.error('❌ Failed to track event:', name, error);
    }
  }

  /**
   * Identify user and set user properties
   */
  identify(userId: string, traits: UserTraits) {
    if (!this.initialized) {
      console.warn('⚠️ Analytics not initialized, cannot identify user');
      return;
    }

    try {
      // Set user in Amplitude
      if (this.amplitudeEnabled) {
        const identifyEvent = new amplitude.Identify();
        
        Object.entries(traits).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            identifyEvent.set(key, value);
          }
        });

        amplitude.identify(identifyEvent);
        amplitude.setUserId(userId);
      }

      // Set user in Firebase
      if (this.firebaseEnabled && this.firebaseAnalytics) {
        firebaseSetUserId(this.firebaseAnalytics, userId);
        firebaseSetUserProperties(this.firebaseAnalytics, traits);
      }

      console.log('👤 User identified:', userId);
    } catch (error) {
      console.error('❌ Failed to identify user:', error);
    }
  }

  /**
   * Update user properties
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized) {
      return;
    }

    try {
      // Update in Amplitude
      if (this.amplitudeEnabled) {
        const identifyEvent = new amplitude.Identify();
        
        Object.entries(properties).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            identifyEvent.set(key, value);
          }
        });

        amplitude.identify(identifyEvent);
      }

      // Update in Firebase
      if (this.firebaseEnabled && this.firebaseAnalytics) {
        firebaseSetUserProperties(this.firebaseAnalytics, properties);
      }

      if (import.meta.env.DEV) {
        console.log('👤 User properties updated:', properties);
      }
    } catch (error) {
      console.error('❌ Failed to set user properties:', error);
    }
  }

  /**
   * Increment a user property
   */
  incrementUserProperty(property: string, value: number = 1) {
    if (!this.initialized || !this.amplitudeEnabled) {
      return;
    }

    try {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.add(property, value);
      amplitude.identify(identifyEvent);
    } catch (error) {
      console.error('❌ Failed to increment user property:', error);
    }
  }

  /**
   * Reset user data (on logout)
   */
  reset() {
    if (!this.initialized) {
      return;
    }

    try {
      if (this.amplitudeEnabled) {
        amplitude.reset();
      }

      // Firebase doesn't have a reset method, but we can clear user ID
      if (this.firebaseEnabled && this.firebaseAnalytics) {
        firebaseSetUserId(this.firebaseAnalytics, null as any);
      }

      console.log('🧹 Analytics user data reset');
    } catch (error) {
      console.error('❌ Failed to reset analytics:', error);
    }
  }

  /**
   * Sanitize event name for Firebase (max 40 chars, alphanumeric + underscore)
   */
  private sanitizeEventName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .substring(0, 40);
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Check if consent is given
   */
  hasConsent(): boolean {
    return this.consentGiven;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;


